import React, { useState } from 'react';
import {
  useNotifications,
  type NotificationId,
} from '../components/notifications/NotificationProvider';
import { showSdCardErrorNotification } from './upload-notifications';
import { useFileUpload } from './use-file-upload';
import { packSdCard } from '../utils/sd-card/pack-sd-card';
import { downloadFile } from '../utils/download-file';

// `webkitdirectory` isn't a typed JSX attribute, so set it (plus the Firefox/
// standard aliases) on the mounted input via a ref callback.
const enableDirectoryPicker = (input: HTMLInputElement | null): void => {
  if (!input) return;
  input.setAttribute('webkitdirectory', '');
  input.setAttribute('directory', '');
};

export const ImportSdCardButton: React.FC = () => {
  const notifications = useNotifications();
  const uploadFiles = useFileUpload();
  const [isBusy, setIsBusy] = useState(false);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      event.target.value = '';
      return;
    }

    setIsBusy(true);
    let packingNotificationId: NotificationId | null = notifications.showUpload({
      title: 'Packing SD card...',
      progress: 1,
    });

    try {
      const packed = await packSdCard(files, (percent) => {
        if (packingNotificationId) {
          notifications.updateUpload(packingNotificationId, {
            progress: Math.min(99, Math.max(1, percent)),
          });
        }
      });

      notifications.close(packingNotificationId);
      packingNotificationId = null;

      for (const warning of packed.warnings) {
        notifications.showWarning(warning);
      }

      downloadFile(packed.file);

      await uploadFiles([packed.file]);
    } catch (error) {
      if (packingNotificationId) notifications.close(packingNotificationId);
      showSdCardErrorNotification(notifications, error);
    } finally {
      setIsBusy(false);
      event.target.value = '';
    }
  };

  return (
    <label className='topbar__btn topbar__btn-secondary' aria-disabled={isBusy}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='20'
        height='20'
        viewBox='0 -960 960 960'
        fill='#096DD9'
      >
        <path d='m480-576 144 144-51 51-57-57v150h-72v-150l-57 57-51-51 144-144ZM263.72-96Q234-96 213-117.15T192-168v-504l192-192h312q29.7 0 50.85 21.15Q768-821.7 768-792v624q0 29.7-21.16 50.85Q725.68-96 695.96-96H263.72Zm.28-72h432v-624H414L264-642v474Zm0 0h432-432Z' />
      </svg>
      <span>{isBusy ? 'Packing SD card...' : 'Import SD card'}</span>
      <input
        ref={enableDirectoryPicker}
        type='file'
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </label>
  );
};
